"use client";

import { useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, Shield, Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import { createClient } from "@oxpa/shared/lib/supabase/client";
import { Button } from "@oxpa/shared/ui/button";

// # START_FUNCTION_LoginFormInner
function LoginFormInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/cabinet";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const supabase = createClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message === "Invalid login credentials"
          ? "Неверный email или пароль"
          : signInError.message
        );
        return;
      }

      router.push(redirectTo);
      router.refresh();
    } catch {
      setError("Произошла непредвиденная ошибка. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex-1 flex items-center justify-center py-20 px-6">
      <div className="w-full max-w-md">
        {/* Breadcrumbs */}
        <nav className="mb-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/50">
            <Link href="/" className="hover:text-white transition-colors">
              Главная
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-white/90">Вход</span>
          </div>
        </nav>

        <div className="border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-5 h-5 text-green-400" />
            <h1 className="text-xl font-bold uppercase tracking-wide">
              Вход в кабинет
            </h1>
          </div>

          <p className="text-sm text-white/50 font-sans mb-8 leading-relaxed">
            Войдите в кабинет владельца профиля ЧОП, чтобы управлять
            репутацией, отвечать на отзывы и редактировать информацию о компании.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[10px] text-white/50 uppercase tracking-widest mb-2">
                Электронная почта
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="director@chop-shield.ru"
                required
                className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/20 px-4 py-3 font-sans text-sm focus:outline-none focus:border-green-500/50 transition-colors"
              />
            </div>

            <div>
              <label className="block text-[10px] text-white/50 uppercase tracking-widest mb-2">
                Пароль
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="******"
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 text-white placeholder:text-white/20 px-4 py-3 pr-10 font-sans text-sm focus:outline-none focus:border-green-500/50 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors focus:outline-none"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-sans px-4 py-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none h-14 bg-green-500 text-black hover:bg-green-400 uppercase tracking-[0.15em] text-sm font-bold disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Вход...
                </>
              ) : (
                "Войти"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-[10px] text-white/30 uppercase tracking-widest font-sans">
              Нет аккаунта?{" "}
              <Link
                href="/register"
                className="text-green-400 hover:text-green-300 transition-colors underline underline-offset-4 decoration-green-400/30"
              >
                Зарегистрироваться
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
// # END_FUNCTION_LoginFormInner

// # START_FUNCTION_LoginPage
export default function PortalLoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      }
    >
      <LoginFormInner />
    </Suspense>
  );
}
// # END_FUNCTION_LoginPage